// Page: usage-bayou-ach-verification  (Bayou onboarding + ACH deposit verify variant)
//
// Ported from the inline <script> in
//   Enrollment_modules/usage-bayou-ach-verification/form.module/module.html (lines 116–2964).
//
// MECHANICAL / FAITHFUL PORT — runtime behaviour is preserved. Deliberate changes:
//   1. Selectors scoped to the render wrapper `<div class="lgsiayeu-main">` are now
//      scoped to `root` (the element carrying data-ea-module) via q/qi/qa. Kept
//      document-scoped, exactly as the original family (usage.ts / entry-il-found.ts):
//      `document.querySelector('body')`, `document.querySelectorAll('button')`/`('a')`;
//      the loader/overlay getElementById lookups `myUsageLoader`/`MUloaderOverlay`/
//      `PassUpdatedresetPassLoader`/`PassUpdatedloaderOverlay` (via `di`); the full-page
//      progress-bar overlays `#progressBar`/`#progressBarOverlay`/`.progressBar`/
//      `.Loading`/`.progressText`/`.progMessage` (via `dq`); and the cross-widget
//      selectors that are NOT inside this module's markup — `#SkipThisFornNow` and
//      `.page1lmitoptitle-content.page2lgsiaeu-content h4` (via `dq`).
//   2. `_hcms/api/*` fetches use core/api `apiUrl`; same-origin redirects are
//      ROOT-RELATIVE ("/" and "/thank-you") instead of hardcoding
//      https://enrollment.nautilussolar.com.
//   3. HubDB read URLs are built with core/hubdb `hubdbRowsUrl` (key `uanConfig`)
//      instead of inline literals.
//   4. localStorage goes through core/storage. Keys outside the StorageKey union
//      use raw*: `bayou_contact_id`, `bayou_reason`, `BayouContactId`,
//      `is_enrolled_for_bayou`, `typ`, `utilityProvider`, `DealStage`,
//      `uan_2_internal_value`.
//   5. Two EXTERNAL non-portal host families are kept as literal absolute URLs
//      (faithful — no __ENV__ key exists for either; inventing one is scope creep),
//      flagged, not rewritten:
//        • https://hansen.danmarcrm.com/*.php — Bayou/account calls
//          (getAccountNumbers.php, getContactByID.php, bayouCreateCustomer.php).
//          NOTE: this variant uses a LITERAL host for bayouCreateCustomer.php, so
//          (unlike entry-il-found.ts) window.__ENV__.bayouApiUrl is NOT used here.
//        • https://stripe.nautilussolar.com/stripeserver/* — the ACH/Stripe payment
//          backend (/ping, /verify/setup-intent). Plain fetch, no Stripe() JS SDK.
//   6. `Bayou` is the CDN global from the `js.bayou.energy/v1` <script src>; it is
//      NOT declared in the theme-root global.d.ts. Per the porting constraint we do
//      NOT edit global.d.ts — it is declared locally here as `any` (matching the
//      per-file `declare` precedent in savings-intro.ts). Flagged for the coordinator
//      to promote to global.d.ts alongside Swiper/pdfjsLib if desired.
//   7. No inline onclick/ondrop/ondragover handlers exist in the markup (all wiring
//      was addEventListener in the inline script), so nothing is re-bound and no page
//      function leaks onto `window`.
//   8. `crypt`/`validatePass` are not referenced by this module's script, so neither
//      is imported. The crypto-js CDN provider + the `var crypt` glue block stay in
//      module.html untouched.
//   9. `// BUGFIX:` two error-path calls to the never-defined `enterZipcloseLoader()`
//      (a latent ReferenceError in GetContactRecord — leftover from a copy-pasted
//      zipcode module) now call the defined `pageFoundCloseLoader()`, which is the
//      clearly-intended "close the loader" behaviour (mirrors the entry-il-found.ts fix).
//  10. `// NOTE:` marks preserved latent bugs: the `if (val = "achSubmit")` assignment
//      (never a comparison) in updateDealStage; and the unreachable localStorage write
//      after `return true` in addDataToDealRecord.
//
// Dynamic ES5 patterns (vars reassigned across types, Bayou/Hansen/Stripe/HubDB row
// shapes, elements augmented with custom props like li.type/li.tariffs) are typed
// `any` to keep the port mechanical.

import { apiUrl } from "../../core/api"
import { hubdbRowsUrl } from "../../core/hubdb"
import * as storage from "../../core/storage"
import { beginOperation, type LoadingOperationHandle } from "../../ui/loading-modal"

// Bayou onboarding widget global from the js.bayou.energy/v1 CDN <script>. Not in
// global.d.ts — declared locally (see header note 6). REPORTED to coordinator.
declare const Bayou: any

export function init(root: HTMLElement, _config: unknown): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","init",[root,_config],["root","_config"]);
  // ─── Scoped DOM helpers ──────────────────────────────────────────────────────
  const q = (sel: string) => root.querySelector(sel) as HTMLElement
  const qi = (sel: string) => root.querySelector(sel) as HTMLInputElement
  const qa = (sel: string) => root.querySelectorAll(sel)
  const di = (id: string) => document.getElementById(id) as HTMLElement
  const dq = (sel: string) => document.querySelector(sel) as HTMLElement

  // ─── Loading modal ─────────────────────────────────────────────────────────
  // Mechanical migration of the former fake-timer progress bar to the shared,
  // accessible loading modal (js/src/ui/loading-modal.ts). Kind "mutation" so a
  // genuine hang shows honest "may still be processing" copy with a Close escape,
  // never a fabricated 100%. The former 15s/30s message swaps and the
  // interval-leak are gone by construction. A deferred audit will split this into
  // read vs mutation withLoader boundaries with abort/idempotency semantics.
  let loaderHandle: LoadingOperationHandle | null = null
  function openProgressBar(message = "We are getting in contact with your utility service provider."): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","openProgressBar",[message],["message"]);
    loaderHandle = beginOperation({ kind: "mutation", message })
  }
  function closeProgressBar(): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","closeProgressBar",[],[]);
    loaderHandle?.complete()
    loaderHandle = null
  }

  let responseFromAPI: any = ""
  // If user is not logged in redirect them to the login page.
  function getEmail(): boolean | undefined {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","getEmail",[],[]);
    const storedEmail = storage.get("userEmail")
    if (!storedEmail) {
      window.location.href = "/"
      return false
    }
  }

  // ─── Loaders (full-page overlays — document-scoped via di) ────────────────────
  function pageFoundOpenLoader(): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","pageFoundOpenLoader",[],[]);
    const loader = di("myUsageLoader")
    const body = document.querySelector("body")
    void body
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("MUloaderOverlay").style.display = "block"
    loader.style.display = "flex"
    buttons.forEach((button) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[button],["button"]);
      button.disabled = true
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = true
    })
  }
  function pageFoundCloseLoader(): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","pageFoundCloseLoader",[],[]);
    const loader = di("myUsageLoader")
    const body = document.querySelector("body")
    void body
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("MUloaderOverlay").style.display = "none"
    loader.style.display = "none"
    buttons.forEach((button) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[button],["button"]);
      button.disabled = false
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = false
    })
  }
  function passUpdatedOpenLoader(): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","passUpdatedOpenLoader",[],[]);
    const loader = di("PassUpdatedresetPassLoader")
    const body = document.querySelector("body")
    void body
    const buttons = document.querySelectorAll("button")
    const anchors = document.querySelectorAll("a")
    di("PassUpdatedloaderOverlay").style.display = "block"
    loader.style.display = "flex"
    buttons.forEach((button) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[button],["button"]);
      button.disabled = true
    })
    anchors.forEach((anchor) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[anchor],["anchor"]);
      ;(anchor as unknown as HTMLButtonElement).disabled = true
    })
  }

  // ─── Shared state (was module-scoped / hoisted in the inline script) ──────────
  let globPass: any
  void globPass
  let verifyidenty: any
  let enrollmentSessionID: any
  const bayouFormCompleted = false
  void bayouFormCompleted
  let bayouToken: any = null
  let bayouTokenType: any = null
  let bayouUtility: any = null
  let bayouId: any = null
  let bayouAccountNo: any = null
  let firstId: any = null
  let meterId: any = null
  let meterType: any = null
  let rateClass: any = null
  let additionalAttributeItem: any = null
  let additionalAttributeValue: any = null
  let accountNofuncCallCount = 0
  let mfaRequired = "No"
  let premisesId: any = null
  let premisesAddress = ""
  let accountNumberResponse: any = ""

  let billingStatus: any = ""
  void billingStatus

  let isUsageSubmit = false
  let isACHSubmit = false

  // utilityNumberRegexFromSessionTable
  const UtilityAccountNumberRegexErrorMsg: any[] = []
  const utRgxNumber: any[] = []
  let utRgxNumberInput: any
  let UtilityAccountNumberRegexErrorMsgInput: any
  let uanSingleValue = ""
  let uanSingleErrorMsgField: any
  let _clickutilityAccountInputs: any
  const allAccountNumbers: any[] = []
  const uanAllLabels: any[] = []
  const uanFieldDefault: any[] = []
  const uanFieldDisplayarray: any[] = []
  const uanFieldIntDesArray: any[] = []

  const utilityIdd = storage.get("utility_id")
  void utilityIdd

  // ACH payment method (populated from the deal record)
  let paymentMethodId = ""
  void paymentMethodId
  let paymentMethod = ""
  void paymentMethod
  let paymentId = ""

  // Read once at init (mirrors the module-level var in the inline script — set after
  // GetContactRecord's async kickoff, so it reflects the pre-existing localStorage value).
  const is_enrolled_for_bayou = storage.rawGet("is_enrolled_for_bayou")

  function getValidTariff(meter: any): any {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","getValidTariff",[meter],["meter"]);
    if (!meter.tariffs || meter.tariffs.length === 0) {
      return null // no tariffs at all
    }

    let selectedTariff: any

    if (meter.tariffs.length === 1) {
      // single tariff
      selectedTariff = meter.tariffs[0]
    } else {
      // multiple tariffs → pick the one with biggest period_to
      selectedTariff = meter.tariffs.reduce((latest: any, current: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[latest,current],["latest","current"]);
        return new Date(current.period_to) > new Date(latest.period_to) ? current : latest
      })
    }

    // check if selected tariff period_to is more than 1 year older than today
    const periodToDate = new Date(selectedTariff.period_to)
    const today = new Date()

    const oneYearLater = new Date(today)
    oneYearLater.setFullYear(today.getFullYear() + 1)

    const oneYearAgo = new Date(today)
    oneYearAgo.setFullYear(today.getFullYear() - 1)

    const isMoreThanOneYearFuture = periodToDate > oneYearLater
    const isOlderThanOneYearPast = periodToDate < oneYearAgo

    // You can control account number visibility here
    const showAccountNumber = isMoreThanOneYearFuture || isOlderThanOneYearPast

    return {
      tariff: selectedTariff,
      isMoreThanOneYear: showAccountNumber,
    }
  }

  /* --------------- for first enrollment ----------------------- */
  async function getAccountNumberByWebhook(token: any, type: any, utility: any, id: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","getAccountNumberByWebhook",[token,type,utility,id],["token","type","utility","id"]);
    const uID = storage.get("utility_id")
    // EXTERNAL Hansen/Bayou service — no __ENV__ key exists, kept as literal.
    const hensonApiUrl = `https://hansen.danmarcrm.com/getAccountNumbers.php?contact_id=${id}`
    const requestBody = {
      contact_id: id,
    }
    let catchdata: any = ""
    try {
      const response = await fetch(hensonApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      })
      catchdata = requestBody
      const responseData = await response.json()
      responseFromAPI = JSON.stringify(responseData)
      accountNumberResponse = JSON.stringify(responseData)
      // Check if there is at least one id
      if (responseData.length > 0) {
        if (responseData.length >= 1) {
          const Accdiv = document.createElement("div")
          const h2 = document.createElement("h2")
          h2.textContent = "Please select Account Number"
          Accdiv.appendChild(h2)
          const accul = document.createElement("ul")
          accul.classList.add("accountNumbersUl")
          await getDataFromUANConfigTable()
          let existAccNoLength = 0
          for (const item of responseData) {
            let accShow = false
            const accli: any = document.createElement("li")
            const accspan = document.createElement("span")
            accli.classList.add(`acc${item.id}`) // Add class to li
            accli.style.display = "none"
            accspan.textContent = item.id
            accli.id = item.id
            accli.appendChild(accspan)
            if (item.meters.length >= 1) {
              const div = document.createElement("div")
              div.classList.add("meterWrapper")
              const h4 = document.createElement("h4")
              h4.textContent = "Please select Additional Attributes"
              div.appendChild(h4)
              div.id = `meterId${item.id}`
              const meterul = document.createElement("ul")
              meterul.id = "meterList"

              /* premises div */
              const premisesDiv = document.createElement("div")
              premisesDiv.classList.add("premisesWrapper")
              premisesDiv.style.display = "none"
              const premisesh4 = document.createElement("h4")
              premisesh4.textContent = "Please select Premises Number"
              premisesDiv.appendChild(premisesh4)
              premisesDiv.id = `premisesId${item.id}`
              const premisesul = document.createElement("ul")
              premisesul.id = "premisesList"
              /* premises div */
              item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[meter],["meter"]);
                const meterli: any = document.createElement("li")
                const premisesli: any = document.createElement("li")
                let mtType = meter.type
                mtType = mtType.toUpperCase()
                if (mtType === "ELECTRIC") {
                  accli.style.display = "block"
                  meterli.id = `${meter.id}`
                  meterli.type = meter.type
                  meterli.setAttribute("tariff", meter.tariffs[0].tariff)
                  if (meter.additional_attributes != null) {
                    meterli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
                  }
                  if (meter.additional_attributes != null) {
                    premisesli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
                  }
                  meterId = `${meter.id}`
                  meterType = meter.type
                  rateClass = meter.tariffs[0].tariff
                  const resultT = getValidTariff(meter)
                  if (accShow === false) {
                    if (resultT.isMoreThanOneYear === true) {
                      accShow = false
                      accli.style.display = "none" // hide
                    } else {
                      accShow = true
                      accli.style.display = "block" // show
                    }
                  }
                  if (uID !== "XCEL-CO-SF") {
                    if (meter.additional_attributes != null) {
                      div.classList.remove("hidden")
                      if (
                        additionalAttributeValue !== "" &&
                        additionalAttributeValue != null &&
                        additionalAttributeValue !== undefined
                      ) {
                        if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                          div.classList.add("hidden")
                        } else {
                          div.classList.remove("hidden")
                        }
                      } else {
                        div.classList.add("hidden")
                      }
                      additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                    } else {
                      div.classList.add("hidden")
                    }
                  } else {
                    if (resultT.isMoreThanOneYear !== true) {
                      if (meter.additional_attributes != null) {
                        if (
                          additionalAttributeValue !== "" &&
                          additionalAttributeValue != null &&
                          additionalAttributeValue !== undefined
                        ) {
                          if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                            // (original left this branch empty)
                          } else {
                            additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                            premisesId = meter.additional_attributes[additionalAttributeItem]
                            premisesAddress = meter.address.line_1
                            premisesli.innerHTML = "Premises ID: " + premisesId + " <br>Address: " + premisesAddress
                            premisesul.appendChild(premisesli)
                            premisesDiv.appendChild(premisesul)
                            accli.appendChild(premisesDiv)
                          }
                        } else {
                          additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                          premisesId = meter.additional_attributes[additionalAttributeItem]
                          premisesAddress = meter.address.line_1
                          premisesli.innerHTML = "Premises ID: " + premisesId + " <br>Address: " + premisesAddress
                          premisesul.appendChild(premisesli)
                          premisesDiv.appendChild(premisesul)
                          accli.appendChild(premisesDiv)
                        }
                      }
                    }
                  }
                  // meterli.textContent = `${meter.id}`;
                  meterli.textContent = additionalAttributeValue
                  meterul.appendChild(meterli)
                  div.appendChild(meterul)
                  accli.appendChild(div)
                }
              })
            }

            async function checkUAN1(val: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","checkUAN1",[val],["val"]);
              const property = "uan_1_value"
              const value = val
              const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ property, value }),
              })
              const data = await response.json()
              responseFromAPI = JSON.stringify(data)
              if (response.ok) {
                if (data.total === 0 || data.total === undefined) {
                  // (original left this branch empty)
                } else if (data.total === 1) {
                  const dealIdFromDealFound = data.results[0].id
                  const dealIdFromCurrentPage = storage.get("dealId")
                  if (dealIdFromDealFound === dealIdFromCurrentPage) {
                    // (original left this branch empty)
                  } else {
                    ++existAccNoLength
                    accli.style.cursor = "not-allowed"
                    accli.style.pointerEvents = "none"
                    accli.style.color = "#999"
                    accli.style.opacity = "0.5"
                  }
                } else {
                  ++existAccNoLength
                  accli.style.cursor = "not-allowed"
                  accli.style.pointerEvents = "none"
                  accli.style.color = "#999"
                  accli.style.opacity = "0.5"
                }
              }
            }
            async function checkUAN1andUAN2(prop: any, val: any): Promise<any> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","checkUAN1andUAN2",[prop,val],["prop","val"]);
              const property = prop
              const value = val
              const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ property, value }),
              })
              const data = await response.json()

              responseFromAPI = JSON.stringify(data)
              return data
              //                             if (response.ok) {
              //                                 if(data.total == 0 || data.total == undefined){

              //                                 }
              //                                 else if(data.total == 1){
              //                                     var dealIdFromDealFound = data.results[0].id;
              //                                     var dealIdFromCurrentPage = localStorage.getItem('dealId');
              //                                     if(dealIdFromDealFound == dealIdFromCurrentPage){

              //                                     }
              //                                     else{
              //                                         ++existAccNoLength;
              //                                         accli.style.cursor = 'not-allowed';
              //                                         accli.style.pointerEvents = 'none';
              //                                         accli.style.color = '#999';
              //                                         accli.style.opacity = '0.5';
              //                                     }
              //                                 }
              //                                 else{
              //                                     ++existAccNoLength;
              //                                     accli.style.cursor = 'not-allowed';
              //                                     accli.style.pointerEvents = 'none';
              //                                     accli.style.color = '#999';
              //                                     accli.style.opacity = '0.5';
              //                                 }
              //                             }
              //                             else{
              //                             }
            }
            if (uID !== "XCEL-CO-SF") {
              await checkUAN1(item.id)
            } else {
              const un1val = await checkUAN1andUAN2("uan_1_value", item.id)
              const un2val = await checkUAN1andUAN2("uan_2_value", additionalAttributeValue)

              if (
                (un1val.total === 0 || un1val.total === undefined) &&
                (un2val.total === 0 || un2val.total === undefined)
              ) {
              } else if (un1val.total === 1 && un2val.total === 1) {
                const dealIdFromDealFoundUAN1 = un1val.results[0].id
                const dealIdFromDealFoundUAN2 = un2val.results[0].id
                const dealIdFromCurrentPage = storage.get("dealId")
                if (
                  dealIdFromDealFoundUAN1 === dealIdFromCurrentPage &&
                  dealIdFromDealFoundUAN2 === dealIdFromCurrentPage
                ) {
                } else {
                  ++existAccNoLength
                  accli.style.cursor = "not-allowed"
                  accli.style.pointerEvents = "none"
                  accli.style.color = "#999"
                  accli.style.opacity = "0.5"
                }
              } else if (un1val.total >= 1 && un2val.total === 0) {
              } else if (un1val.total === 0 && un2val.total >= 1) {
              } else if (un1val.total >= 1 && un2val.total >= 1) {
                ++existAccNoLength
                accli.style.cursor = "not-allowed"
                accli.style.pointerEvents = "none"
                accli.style.color = "#999"
                accli.style.opacity = "0.5"
              } else {
                ++existAccNoLength
                accli.style.cursor = "not-allowed"
                accli.style.pointerEvents = "none"
                accli.style.color = "#999"
                accli.style.opacity = "0.5"
              }
            }
            accul.appendChild(accli)
            Accdiv.appendChild(accul)
            q(".have-online-details").appendChild(Accdiv)
          }
          closeProgressBar()
          if (existAccNoLength === responseData.length) {
            verifyidenty = "no"
            // alert("Account number already exists. Please try again.");
            passUpdatedOpenLoader()
          }
          qa(".meterWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
            li.style.display = "none"
          })
          function accountNumberSelected(event: any): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","accountNumberSelected",[event],["event"]);
            qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
              span.style.backgroundColor = "#ffffff"
            })
            qa(".meterWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
              li.style.display = "none"
            })
            if (uID === "XCEL-CO-SF") {
              qa(".premisesWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                li.style.display = "none"
              })
            }
            const clickedElement = event.target
            firstId = clickedElement.parentElement.id
            bayouAccountNo = firstId
            const meterWrapper = clickedElement.parentElement.querySelector(".meterWrapper")
            let premisesWrapper: any
            if (uID === "XCEL-CO-SF") {
              premisesWrapper = clickedElement.parentElement.querySelector(".premisesWrapper")
            }
            if (!meterWrapper.classList.contains("hidden")) {
              if (uID !== "XCEL-CO-SF") {
                const ulElement = meterWrapper.querySelector("ul")
                const liElements = ulElement.querySelectorAll("li")
                const numberOfLi = liElements.length
                if (numberOfLi === 1) {
                  verifyidenty = "yes"
                  meterWrapper.style.display = "none"
                  liElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                    meterId = li.id
                    meterType = li.type
                    if (li.tariffs) {
                      rateClass = li.tariffs[0].tariff
                    }
                    additionalAttributeValue = li.getAttribute("aa")
                  })
                } else {
                  ulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                    span.style.backgroundColor = "#ffffff"
                  })
                  meterWrapper.style.display = "block"
                  ulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                    li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[this],["this"]);
                      verifyidenty = "yes"
                      ulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                        span.style.backgroundColor = "#ffffff"
                      })
                      this.style.backgroundColor = "#f18020"
                      meterId = this.id
                      meterType = this.type
                      if (this.tariffs) {
                        rateClass = this.tariffs[0].tariff
                      }
                      additionalAttributeValue = this.getAttribute("aa")
                    })
                  })
                }
              } else {
                const premisesulElement = premisesWrapper.querySelector("ul")
                const premisesliElements = premisesulElement.querySelectorAll("li")
                const numberOfPreLi = premisesliElements.length
                if (numberOfPreLi === 1) {
                  verifyidenty = "yes"
                  premisesWrapper.style.display = "none"
                  premisesliElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                    //                                     meterId = li.id;
                    //                                     meterType = li.type;
                    //                                     if(li.tariffs){
                    //                                         rateClass = li.tariffs[0].tariff;
                    //                                     }
                    additionalAttributeValue = li.getAttribute("aa")
                  })
                } else {
                  premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                    span.style.backgroundColor = "#ffffff"
                    span.style.color = "#425b76"
                  })
                  premisesWrapper.style.display = "block"
                  premisesulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                    li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[this],["this"]);
                      verifyidenty = "yes"
                      premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                        span.style.backgroundColor = "#ffffff"
                        span.style.color = "#425b76"
                      })
                      this.style.backgroundColor = "#f18020"
                      this.style.color = "#ffffff"
                      //                                         meterId = this.id;
                      //                                         meterType = this.type;
                      //                                         if(this.tariffs){
                      //                                             rateClass = this.tariffs[0].tariff;
                      //                                         }
                      additionalAttributeValue = this.getAttribute("aa")
                    })
                  })
                }
              }
            } else {
              if (uID === "XCEL-CO-SF") {
                const premisesulElement = premisesWrapper.querySelector("ul")
                const premisesliElements = premisesulElement.querySelectorAll("li")
                const numberOfPreLi = premisesliElements.length
                if (numberOfPreLi === 1) {
                  verifyidenty = "yes"
                  premisesWrapper.style.display = "none"
                  premisesliElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                    //                                     meterId = li.id;
                    //                                     meterType = li.type;
                    //                                     if(li.tariffs){
                    //                                         rateClass = li.tariffs[0].tariff;
                    //                                     }
                    additionalAttributeValue = li.getAttribute("aa")
                  })
                } else {
                  premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                    span.style.backgroundColor = "#ffffff"
                    span.style.color = "#425b76"
                  })
                  premisesWrapper.style.display = "block"
                  premisesulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                    li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[this],["this"]);
                      verifyidenty = "yes"
                      premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                        span.style.backgroundColor = "#ffffff"
                        span.style.color = "#425b76"
                      })
                      this.style.backgroundColor = "#f18020"
                      this.style.color = "#ffffff"
                      //                                         meterId = this.id;
                      //                                         meterType = this.type;
                      //                                         if(this.tariffs){
                      //                                             rateClass = this.tariffs[0].tariff;
                      //                                         }
                      additionalAttributeValue = this.getAttribute("aa")
                    })
                  })
                }
              } else {
                verifyidenty = "yes"
              }
            }
            clickedElement.style.backgroundColor = "#f18020"
            qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
              span.removeAttribute("data-selected")
            })
            clickedElement.setAttribute("data-selected", "true")
          }
          qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
            span.addEventListener("click", accountNumberSelected)
          })
        }
        bayouToken = token
        bayouTokenType = type
        bayouUtility = utility
        bayouId = id
        bayouAccountNo = firstId
        pageFoundCloseLoader()
      } else {
        if (accountNofuncCallCount === 50) {
          pageFoundCloseLoader()
          closeProgressBar()
          verifyidenty = "no"
          alert(
            "We successfully validated your credentials but were unable to find any Electric accounts. If you recently created your utility login, please try again later.",
          )
          // passUpdatedOpenLoader();
        } else {
          ++accountNofuncCallCount
          await getAccountNumberByWebhook(token, type, utility, id)
        }
      }
    } catch (error) {
      closeProgressBar()
      pageFoundCloseLoader()
      console.error("Error calling API:", error)
    }
  }

  async function updateBayouResponseInDeal(token: any, type: any, utility: any, id: any, firstId: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","updateBayouResponseInDeal",[token,type,utility,id,firstId],["token","type","utility","id","firstId"]);
    void token
    void type
    void utility
    void id
    void firstId
    const dealId = storage.get("dealId")
    pageFoundOpenLoader()
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          bayous_utility_id: bayouUtility,
          bayous_type: bayouTokenType,
          bayous_id: bayouId,
          bayous_onboarding_token: bayouToken,
          uan_1_label: "Bayou's Utility Id",
          uan_1_value: bayouAccountNo,
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        pageFoundCloseLoader()
      }
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      pageFoundCloseLoader()
      // alert('Server error. Please try again');
      console.error("Error:", error)
    }
  }
  void updateBayouResponseInDeal

  async function getTokenAndDateStamp(): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","getTokenAndDateStamp",[],[]);
    const email = storage.get("userEmail")
    let catchdata: any = ""
    try {
      const data = {
        email: email,
      }
      const response = await fetch(apiUrl("GetContactRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      const dateStamp = dataa.userValue.properties.date_stamp
      const bToken = dataa.userValue.properties.bayou_onboarding_token
      void bToken
      const bContactId = dataa.userValue.properties.bayou_contact_id
      storage.set("dateStamp", dateStamp)
      storage.rawSet("bayou_contact_id", bContactId)
      // localStorage.setItem("LoggedName", firstName + " " + lastName);
    } catch (error) {
      console.error("Error:", error)
    }
  }

  async function updateTokenAndDateInContact(token: any, date: any, tokenLink: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","updateTokenAndDateInContact",[token,date,tokenLink],["token","date","tokenLink"]);
    const email = storage.get("userEmail")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          email: email,
          bayou_onboarding_token: token,
          date_stamp: date,
          bayou_onboarding_link: tokenLink,
        },
      }
      const response = await fetch(apiUrl("UpdateContactRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      responseFromAPI = await response.json()
      responseFromAPI = JSON.stringify(responseFromAPI)
    } catch (error) {
      console.error("Error:", error)
    }
  }

  function getCurrentDate(): string {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","getCurrentDate",[],[]);
    const today = new Date()
    const month = String(today.getMonth() + 1).padStart(2, "0") // Months are 0-based
    const day = String(today.getDate()).padStart(2, "0")
    const year = today.getFullYear()
    return `${month}/${day}/${year}`
  }

  async function BayouGetCustomersWhenTokenExpire(): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","BayouGetCustomersWhenTokenExpire",[],[]);
    const bayou_reason = storage.rawGet("bayou_reason")
    const bayouContactId = storage.rawGet("bayou_contact_id")
    verifyidenty = "no"
    // EXTERNAL Hansen/Bayou service — no __ENV__ key exists, kept as literal.
    const hensonApiUrl = `https://hansen.danmarcrm.com/getContactByID.php?contact_id=${bayouContactId}`
    const requestBody = {
      contact_id: bayouContactId,
    }
    let catchdata: any = ""
    try {
      const response = await fetch(hensonApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      })
      catchdata = requestBody
      const responseData = await response.json()
      responseFromAPI = JSON.stringify(responseData)
      const onboardingToken = responseData.onboarding_token
      const onboardingLink = responseData.onboarding_link
      storage.set("onboarding_token", onboardingToken)
      storage.set("dateStamp", getCurrentDate())
      await updateTokenAndDateInContact(onboardingToken, getCurrentDate(), onboardingLink)
      Bayou.loadOnboardingForm(
        q("#have-online-details"),
        60, // Found in your company settings: https://staging.bayou.energy/dashboard/settings
        onboardingToken, // Generated using this API call: https://docs.bayou.energy/v2.0/reference/get_customers-id
        (event: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[event],["event"]);
          q("#verification-error-message").innerHTML = ""
          if (event.type === "customerHasAuthenticated") {
            mfaRequired = "No"
            dq("#SkipThisFornNow").style.display = "none"
            storage.rawSet("BayouContactId", event.data.id)
            openProgressBar()
            // alert("Customer has completed the form");
            setTimeout(() => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[],[]);
              getAccountNumberByWebhook(onboardingToken, event.type, event.data.utility, event.data.id)
            }, 10000)
          } else if (event.type === "customerEnteredInvalidCredentials") {
            mfaRequired = "No"
            dq("#SkipThisFornNow").style.display = "block"
            alert("Customer entered invalid credentials")
            verifyidenty = "no"
          } else if (event.type === "customerEnteredInvalidMFAChallenge") {
            mfaRequired = "Yes"
            dq("#SkipThisFornNow").style.display = "block"
            alert("Customer entered invalid MFA challenge")
            verifyidenty = "no"
          }
        },
      )
    } catch (error) {
      verifyidenty = "no"
      void catchdata
      pageFoundCloseLoader()
      console.error("Error calling API:", error)
    }
  }

  async function BayouCreateCustomer(onbToken: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","BayouCreateCustomer",[onbToken],["onbToken"]);
    const UtilitySessionDetails = JSON.parse(storage.get("UtilitySessionDetails") ?? "null")
    const ContactId = storage.get("RecordId")
    verifyidenty = "no"
    const dealId = storage.get("dealId")
    // EXTERNAL Hansen/Bayou service — no __ENV__ key exists, kept as literal.
    const _hensonApiUrl = "https://hansen.danmarcrm.com/bayouCreateCustomer.php"
    const requestBody = {
      utility: UtilitySessionDetails["9"],
      contact_id: ContactId,
      external_id: dealId,
    }
    void requestBody
    // var onbToken = localStorage.getItem("onboarding_token");
    if (onbToken == null || onbToken === "null") {
      onbToken = storage.get("onboarding_token")
    }
    await getTokenAndDateStamp()
    let tokenCreatedDate = storage.get("dateStamp")
    void tokenCreatedDate
    const currentDate = getCurrentDate()
    void currentDate
    function isNineDaysPassed(tokenCreatedDate: any): boolean {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","isNineDaysPassed",[tokenCreatedDate],["tokenCreatedDate"]);
      const created = new Date(tokenCreatedDate)
      const today = new Date()

      // Calculate difference in milliseconds
      const diffInMs = (today as any) - (created as any)

      // Convert to days
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

      return diffInDays >= 9
    }

    tokenCreatedDate = storage.get("dateStamp") // e.g., "04/28/2025"
    if (tokenCreatedDate && isNineDaysPassed(tokenCreatedDate)) {
      await BayouGetCustomersWhenTokenExpire()
    } else {
      Bayou.loadOnboardingForm(
        q("#have-online-details"),
        60, // Found in your company settings: https://staging.bayou.energy/dashboard/settings
        onbToken, // Generated using this API call: https://docs.bayou.energy/v2.0/reference/get_customers-id
        (event: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[event],["event"]);
          q("#verification-error-message").innerHTML = ""
          if (event.type === "customerHasAuthenticated") {
            openProgressBar()
            // alert("Customer has completed the form");
            storage.rawSet("BayouContactId", event.data.id)
            // verifyidenty = 'yes';
            pageFoundOpenLoader()
            setTimeout(() => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[],[]);
              getAccountNumberByWebhook(onbToken, event.type, event.data.utility, event.data.id)
            }, 10000)
          } else if (event.type === "customerEnteredInvalidCredentials") {
            // document.querySelector('#SkipThisFornNow').style.display = "block";
            mfaRequired = "No"
            alert("Customer entered invalid credentials")
            verifyidenty = "no"
          } else if (event.type === "customerEnteredInvalidMFAChallenge") {
            mfaRequired = "Yes"
            // document.querySelector('#SkipThisFornNow').style.display = "block";
            alert("Customer entered invalid MFA challenge")
            verifyidenty = "no"
          }
          //                 else{
          //                     alert("Invalid Credentials");
          //                     verifyidenty = 'no';
          //                 }
        },
      )
    }
    pageFoundCloseLoader()
  }

  async function GetDataFromDealRecord(): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","GetDataFromDealRecord",[],[]);
    pageFoundOpenLoader()
    // var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        deal_id: dealId,
      }
      const response = await fetch(apiUrl("GetDealRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      if (dataa.message === "No User Found") {
      } else if (dataa.message === "User Found") {
        const doYouHaveAccount = dataa.userValue.properties.do_you_have_an_account_with_utility_service_provider
        void doYouHaveAccount
        const dealStage = dataa.userValue.properties.dealstage
        const onbToken = dataa.userValue.properties.bayous_onboarding_token
        if (dealStage === "127892571") {
          // Verify Utility Info (Sales Pipeline)
          q(".lgsiayeu-form").style.display = "block"
          q(".combinedAchWrapper").style.display = "none"
        } else if (dealStage === "1092432407") {
          // Verify ACH (Sales Pipeline)
          q(".combinedAchWrapper").style.display = "block"
          q(".lgsiayeu-form").style.display = "none"
        } else if (dealStage === "1100007032") {
          // Verify Utility Info/Verify ACH (Sales Pipeline)
          q(".combinedAchWrapper").style.display = "block"
          q(".lgsiayeu-form").style.display = "block"
        }

        await BayouCreateCustomer(onbToken)
      }
    } catch (error) {
      console.error("Error:", error)
    }
  }
  /* --------------- for first enrollment ----------------------- */

  /* --------------- for multi enrollment ----------------------- */
  async function GetDataFromDealRecordMulti(onboardToken: any, bayouContactId: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","GetDataFromDealRecordMulti",[onboardToken,bayouContactId],["onboardToken","bayouContactId"]);
    pageFoundOpenLoader()
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        deal_id: dealId,
      }
      const response = await fetch(apiUrl("GetDealRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      if (dataa.message === "No User Found") {
      } else if (dataa.message === "User Found") {
        const doYouHaveAccount = dataa.userValue.properties.do_you_have_an_account_with_utility_service_provider
        void doYouHaveAccount
        const dealStage = dataa.userValue.properties.dealstage
        if (dealStage === "127892571") {
          // Verify Utility Info (Sales Pipeline)
          q(".lgsiayeu-form").style.display = "block"
          q(".combinedAchWrapper").style.display = "none"
        } else if (dealStage === "1092432407") {
          // Verify ACH (Sales Pipeline)
          q(".combinedAchWrapper").style.display = "block"
          q(".lgsiayeu-form").style.display = "none"
        } else if (dealStage === "1100007032") {
          // Verify Utility Info/Verify ACH (Sales Pipeline)
          q(".combinedAchWrapper").style.display = "block"
          q(".lgsiayeu-form").style.display = "block"
        }

        await BayouGetCustomers(onboardToken, bayouContactId)
      }
    } catch (error) {
      console.error("Error:", error)
    }
  }

  async function BayouGetCustomers(_onboardToken: any, bayouContactId: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","BayouGetCustomers",[_onboardToken,bayouContactId],["_onboardToken","bayouContactId"]);
    const bayou_reason = storage.rawGet("bayou_reason")
    const UtilitySessionDetails = JSON.parse(storage.get("UtilitySessionDetails") ?? "null")
    void UtilitySessionDetails
    // var bayouContactId = localStorage.getItem("bayou_contact_id");
    // verifyidenty = 'no';
    const dealId = storage.get("dealId")
    void dealId
    // EXTERNAL Hansen/Bayou service — no __ENV__ key exists, kept as literal.
    const hensonApiUrl = `https://hansen.danmarcrm.com/getContactByID.php?contact_id=${bayouContactId}`
    const requestBody = {
      contact_id: bayouContactId,
    }
    let catchdata: any = ""
    try {
      const response = await fetch(hensonApiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      })
      catchdata = requestBody
      const responseData = await response.json()
      responseFromAPI = JSON.stringify(responseData)
      if (bayou_reason == null || bayou_reason === undefined || bayou_reason === "" || "null") {
        const accountNumbers = responseData.account_numbers.map((account: any) => account.id)
        void accountNumbers
        const accountDetails = responseData.account_numbers.map((account: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[account],["account"]);
          return {
            account_number: account.id,
            meters: account.meters.map((meter: any) => ({
              id: meter.id,
              type: meter.type,
              tariffs: meter.tariffs,
              address: meter.address,
              additional_attributes: meter.additional_attributes,
            })),
          }
        })
        if (accountDetails.length > 0) {
          if (accountDetails.length === 1) {
            // findsingleAccountNumber(localStorage.getItem("RecordId"), accountDetails[0].account_number, accountDetails);
            findmultiAccountNumber(storage.get("RecordId"), accountDetails, accountDetails)
          } else if (accountDetails.length > 1) {
            findmultiAccountNumber(storage.get("RecordId"), accountDetails, accountDetails)
          }
        }
        pageFoundCloseLoader()
      } else {
        await getTokenAndDateStamp()
        let tokenCreatedDate = storage.get("dateStamp")
        void tokenCreatedDate
        const currentDate = getCurrentDate()
        void currentDate
        function isNineDaysPassed(tokenCreatedDate: any): boolean {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","isNineDaysPassed",[tokenCreatedDate],["tokenCreatedDate"]);
          const created = new Date(tokenCreatedDate)
          const today = new Date()

          // Calculate difference in milliseconds
          const diffInMs = (today as any) - (created as any)

          // Convert to days
          const diffInDays = diffInMs / (1000 * 60 * 60 * 24)

          return diffInDays >= 9
        }

        tokenCreatedDate = storage.get("dateStamp") // e.g., "04/28/2025"
        if (tokenCreatedDate && isNineDaysPassed(tokenCreatedDate)) {
          await BayouGetCustomersWhenTokenExpire()
        } else {
          const onbToken = storage.get("onboarding_token")
          Bayou.loadOnboardingForm(
            q("#have-online-details"),
            60, // Found in your company settings: https://staging.bayou.energy/dashboard/settings
            onbToken, // Generated using this API call: https://docs.bayou.energy/v2.0/reference/get_customers-id
            (event: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[event],["event"]);
              q("#verification-error-message").innerHTML = ""
              if (event.type === "customerHasAuthenticated") {
                openProgressBar()
                dq("#SkipThisFornNow").style.display = "none"
                // alert("Customer has completed the form");
                storage.rawSet("BayouContactId", event.data.id)
                // verifyidenty = 'yes';
                setTimeout(() => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[],[]);
                  getAccountNumberByWebhook(onbToken, event.type, event.data.utility, event.data.id)
                }, 5000)
              } else if (event.type === "customerEnteredInvalidCredentials") {
                // document.querySelector('#SkipThisFornNow').style.display = "block";
                mfaRequired = "No"
                alert("Customer entered invalid credentials")
                verifyidenty = "no"
              } else if (event.type === "customerEnteredInvalidMFAChallenge") {
                mfaRequired = "Yes"
                // document.querySelector('#SkipThisFornNow').style.display = "block";
                alert("Customer entered invalid MFA challenge")
                verifyidenty = "no"
              }
              //                         else{
              //                             document.querySelector('#SkipThisFornNow').style.display = "block";
              //                             alert("Invalid Credentials");
              //                             verifyidenty = 'no';
              //                         }
            },
          )
        }
        pageFoundCloseLoader()
      }
      if (response.status === 500 || response.status === 404) {
        verifyidenty = "no"
        pageFoundCloseLoader()
      }
    } catch (error) {
      verifyidenty = "no"
      pageFoundCloseLoader()
      console.error("Error calling API:", error)
    }
  }

  async function findsingleAccountNumber(contactId: any, accountnumber: any, responseData: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","findsingleAccountNumber",[contactId,accountnumber,responseData],["contactId","accountnumber","responseData"]);
    void contactId
    const property = "uan_1_value"
    const value = accountnumber
    const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ property, value }),
    })
    const data = await response.json()
    responseFromAPI = JSON.stringify(data)
    if (response.ok) {
      if (data.total === 0 || data.total === undefined) {
        await getDataFromUANConfigTable()
        // alert("Account number not exists.");
        firstId = accountnumber
        bayouAccountNo = firstId
        responseData.forEach((item: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[item],["item"]);
          if (item.meters.length === 1) {
            item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[meter],["meter"]);
              verifyidenty = "yes"
              let mtType = meter.type
              mtType = mtType.toUpperCase()
              if (mtType === "ELECTRIC") {
                meterId = meter.id
                meterType = meter.type
                rateClass = meter.tariffs[0].tariff
                if (meter.additional_attributes != null) {
                  additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                }
              } else {
                meterId = ""
                meterType = ""
                rateClass = ""
                additionalAttributeValue = ""
              }
            })
          } else if (item.meters.length > 1) {
            let ifelectricMetermorethan1 = 0
            const div = document.createElement("div")
            div.classList.add("meterWrapper")
            const h4 = document.createElement("h4")
            h4.textContent = "Please select Additional Attributes"
            div.appendChild(h4)
            const meterul = document.createElement("ul")
            meterul.id = "meterList"
            item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[meter],["meter"]);
              const meterli: any = document.createElement("li")
              let mtType = meter.type
              mtType = mtType.toUpperCase()
              if (mtType === "ELECTRIC") {
                meterli.id = `${meter.id}`
                meterli.type = meter.type
                meterli.setAttribute("tariff", meter.tariffs[0].tariff)
                if (meter.additional_attributes != null) {
                  meterli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
                }
                ifelectricMetermorethan1++
                meterId = `${meter.id}`
                meterType = meter.type
                rateClass = meter.tariffs[0].tariff
                if (meter.additional_attributes != null) {
                  div.classList.remove("hidden")
                  if (
                    additionalAttributeValue !== "" &&
                    additionalAttributeValue != null &&
                    additionalAttributeValue !== undefined
                  ) {
                    if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                      div.classList.add("hidden")
                    } else {
                      div.classList.remove("hidden")
                    }
                  } else {
                    div.classList.add("hidden")
                  }
                  additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                } else {
                  div.classList.add("hidden")
                }
                // meterli.textContent = `${meter.id}`;
                meterli.textContent = additionalAttributeValue
                meterul.appendChild(meterli)
              }
            })
            void ifelectricMetermorethan1
            const meterWrapper = q(".meterWrapper")
            if (!meterWrapper.classList.contains("hidden")) {
              div.appendChild(meterul)
              q(".have-online-details").appendChild(div)
              function handleClick(event: any): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","handleClick",[event],["event"]);
                verifyidenty = "yes"
                qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                  li.style.backgroundColor = "#ffffff"
                })
                const clickedElement = event.target
                meterId = clickedElement.id
                meterType = clickedElement.type
                rateClass = clickedElement.tariff
                additionalAttributeValue = clickedElement.getAttribute("aa")
                clickedElement.style.backgroundColor = "#f18020"
                qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                  li.removeAttribute("data-selected")
                })
                clickedElement.setAttribute("data-selected", "true")
              }
              qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                li.addEventListener("click", handleClick)
              })
            } else {
              verifyidenty = "yes"
            }
          }
        })
      } else if (data.total === 1) {
        const dealIdFromDealFound = data.results[0].id
        const dealIdFromCurrentPage = storage.get("dealId")
        if (dealIdFromDealFound === dealIdFromCurrentPage) {
          await getDataFromUANConfigTable()
          // alert("Account number not exists.");
          firstId = accountnumber
          bayouAccountNo = firstId
          responseData.forEach((item: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[item],["item"]);
            if (item.meters.length === 1) {
              item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[meter],["meter"]);
                verifyidenty = "yes"
                let mtType = meter.type
                mtType = mtType.toUpperCase()
                if (mtType === "ELECTRIC") {
                  meterId = meter.id
                  meterType = meter.type
                  rateClass = meter.tariffs[0].tariff
                  if (meter.additional_attributes != null) {
                    additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                  }
                } else {
                  meterId = ""
                  meterType = ""
                  rateClass = ""
                  additionalAttributeValue = ""
                }
              })
            } else if (item.meters.length > 1) {
              let ifelectricMetermorethan1 = 0
              const div = document.createElement("div")
              div.classList.add("meterWrapper")
              const h4 = document.createElement("h4")
              h4.textContent = "Please select Additional Attributes"
              div.appendChild(h4)
              const meterul = document.createElement("ul")
              meterul.id = "meterList"
              item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[meter],["meter"]);
                const meterli: any = document.createElement("li")
                let mtType = meter.type
                mtType = mtType.toUpperCase()
                if (mtType === "ELECTRIC") {
                  meterli.id = `${meter.id}`
                  meterli.type = meter.type
                  meterli.setAttribute("tariff", meter.tariffs[0].tariff)
                  if (meter.additional_attributes != null) {
                    meterli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
                  }
                  ifelectricMetermorethan1++
                  meterId = `${meter.id}`
                  meterType = meter.type
                  rateClass = meter.tariffs[0].tariff
                  if (meter.additional_attributes != null) {
                    div.classList.remove("hidden")
                    if (
                      additionalAttributeValue !== "" &&
                      additionalAttributeValue != null &&
                      additionalAttributeValue !== undefined
                    ) {
                      if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                        div.classList.add("hidden")
                      } else {
                        div.classList.remove("hidden")
                      }
                    } else {
                      div.classList.add("hidden")
                    }
                    additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                  } else {
                    div.classList.add("hidden")
                  }
                  // meterli.textContent = `${meter.id}`;
                  meterli.textContent = additionalAttributeValue
                  meterul.appendChild(meterli)
                }
              })
              void ifelectricMetermorethan1
              const meterWrapper = q(".meterWrapper")
              if (!meterWrapper.classList.contains("hidden")) {
                div.appendChild(meterul)
                q(".have-online-details").appendChild(div)
                function handleClick(event: any): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","handleClick",[event],["event"]);
                  verifyidenty = "yes"
                  qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                    li.style.backgroundColor = "#ffffff"
                  })
                  const clickedElement = event.target
                  meterId = clickedElement.id
                  meterType = clickedElement.type
                  rateClass = clickedElement.tariff
                  additionalAttributeValue = clickedElement.getAttribute("aa")
                  clickedElement.style.backgroundColor = "#f18020"
                  qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                    li.removeAttribute("data-selected")
                  })
                  clickedElement.setAttribute("data-selected", "true")
                }
                qa("#meterList li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
                  li.addEventListener("click", handleClick)
                })
              } else {
                verifyidenty = "yes"
              }
            }
          })
        } else {
          verifyidenty = "no"
          // alert("Account number already exists. Please try again.");
          passUpdatedOpenLoader()
        }
      } else {
        verifyidenty = "no"
        // alert("Account number already exists. Please try again.");
        passUpdatedOpenLoader()
        // return false;
      }
    }
  }
  void findsingleAccountNumber

  async function findmultiAccountNumber(contactId: any, accountnumber: any, responseData: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","findmultiAccountNumber",[contactId,accountnumber,responseData],["contactId","accountnumber","responseData"]);
    void contactId
    void accountnumber
    const uID = storage.get("utility_id")
    await getDataFromUANConfigTable()
    const Accdiv = document.createElement("div")
    const h2 = document.createElement("h2")
    h2.textContent = "Please select Account Number"
    Accdiv.appendChild(h2)
    const accul = document.createElement("ul")
    accul.classList.add("accountNumbersUl")
    let existAccNoLength = 0
    for (const item of responseData) {
      let accShow = false
      const accli: any = document.createElement("li")
      const accspan = document.createElement("span")
      accli.classList.add(`acc${item.account_number}`) // Add class to li
      accli.style.display = "none"
      accspan.textContent = item.account_number
      accli.id = item.account_number
      accli.appendChild(accspan)
      if (item.meters.length >= 1) {
        const div = document.createElement("div")
        div.classList.add("meterWrapper")
        const h4 = document.createElement("h4")
        h4.textContent = "Please select Additional Attributes"
        div.appendChild(h4)
        div.id = `meterId${item.account_number}`
        const meterul = document.createElement("ul")
        meterul.id = "meterList"

        /* premises div */
        const premisesDiv = document.createElement("div")
        premisesDiv.classList.add("premisesWrapper")
        premisesDiv.style.display = "none"
        const premisesh4 = document.createElement("h4")
        premisesh4.textContent = "Please select Premises Number"
        premisesDiv.appendChild(premisesh4)
        premisesDiv.id = `premisesId${item.account_number}`
        const premisesul = document.createElement("ul")
        premisesul.id = "premisesList"
        /* premises div */
        item.meters.forEach((meter: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[meter],["meter"]);
          const meterli: any = document.createElement("li")
          const premisesli: any = document.createElement("li")
          let mtType = meter.type
          mtType = mtType.toUpperCase()
          if (mtType === "ELECTRIC") {
            accli.style.display = "block"
            meterli.id = `${meter.id}`
            meterli.type = meter.type
            meterli.setAttribute("tariff", meter.tariffs[0].tariff)
            if (meter.additional_attributes != null) {
              meterli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
            }
            if (meter.additional_attributes != null) {
              premisesli.setAttribute("aa", meter.additional_attributes[additionalAttributeItem])
            }
            meterId = `${meter.id}`
            meterType = meter.type
            rateClass = meter.tariffs[0].tariff
            const resultT = getValidTariff(meter)
            if (accShow === false) {
              if (resultT.isMoreThanOneYear === true) {
                accShow = false
                accli.style.display = "none" // hide
              } else {
                accShow = true
                accli.style.display = "block" // show
              }
            }
            if (uID !== "XCEL-CO-SF") {
              if (meter.additional_attributes != null) {
                div.classList.remove("hidden")
                if (
                  additionalAttributeValue !== "" &&
                  additionalAttributeValue != null &&
                  additionalAttributeValue !== undefined
                ) {
                  if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                    div.classList.add("hidden")
                  } else {
                    div.classList.remove("hidden")
                  }
                } else {
                  div.classList.add("hidden")
                }
                additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
              } else {
                div.classList.add("hidden")
              }
            } else {
              if (resultT.isMoreThanOneYear !== true) {
                if (meter.additional_attributes != null) {
                  if (
                    additionalAttributeValue !== "" &&
                    additionalAttributeValue != null &&
                    additionalAttributeValue !== undefined
                  ) {
                    if (additionalAttributeValue === meter.additional_attributes[additionalAttributeItem]) {
                      // (original left this branch empty)
                    } else {
                      additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                      premisesId = meter.additional_attributes[additionalAttributeItem]
                      premisesAddress = meter.address.line_1
                      premisesli.innerHTML = "Premises ID: " + premisesId + " <br>Address: " + premisesAddress
                      premisesul.appendChild(premisesli)
                      premisesDiv.appendChild(premisesul)
                      accli.appendChild(premisesDiv)
                    }
                  } else {
                    additionalAttributeValue = meter.additional_attributes[additionalAttributeItem]
                    premisesId = meter.additional_attributes[additionalAttributeItem]
                    premisesAddress = meter.address.line_1
                    premisesli.innerHTML = "Premises ID: " + premisesId + " <br>Address: " + premisesAddress
                    premisesul.appendChild(premisesli)
                    premisesDiv.appendChild(premisesul)
                    accli.appendChild(premisesDiv)
                  }
                }
              }
            }
            // meterli.textContent = `${meter.id}`;
            meterli.textContent = additionalAttributeValue
            meterul.appendChild(meterli)
            div.appendChild(meterul)
            accli.appendChild(div)
          }
        })
      }

      async function checkUAN1(val: any): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","checkUAN1",[val],["val"]);
        const property = "uan_1_value"
        const value = val
        const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ property, value }),
        })
        const data = await response.json()
        responseFromAPI = JSON.stringify(data)
        if (response.ok) {
          if (data.total === 0 || data.total === undefined) {
            // (original left this branch empty)
          } else if (data.total === 1) {
            const dealIdFromDealFound = data.results[0].id
            const dealIdFromCurrentPage = storage.get("dealId")
            if (dealIdFromDealFound === dealIdFromCurrentPage) {
              // (original left this branch empty)
            } else {
              ++existAccNoLength
              accli.style.cursor = "not-allowed"
              accli.style.pointerEvents = "none"
              accli.style.color = "#999"
              accli.style.opacity = "0.5"
            }
          } else {
            ++existAccNoLength
            accli.style.cursor = "not-allowed"
            accli.style.pointerEvents = "none"
            accli.style.color = "#999"
            accli.style.opacity = "0.5"
          }
        }
      }
      async function checkUAN1andUAN2(prop: any, val: any): Promise<any> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","checkUAN1andUAN2",[prop,val],["prop","val"]);
        const property = prop
        const value = val
        const response = await fetch(apiUrl("SearchUANValuesInDealRecord"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ property, value }),
        })
        const data = await response.json()

        responseFromAPI = JSON.stringify(data)
        return data
        //                             if (response.ok) {
        //                                 if(data.total == 0 || data.total == undefined){

        //                                 }
        //                                 else if(data.total == 1){
        //                                     var dealIdFromDealFound = data.results[0].id;
        //                                     var dealIdFromCurrentPage = localStorage.getItem('dealId');
        //                                     if(dealIdFromDealFound == dealIdFromCurrentPage){

        //                                     }
        //                                     else{
        //                                         ++existAccNoLength;
        //                                         accli.style.cursor = 'not-allowed';
        //                                         accli.style.pointerEvents = 'none';
        //                                         accli.style.color = '#999';
        //                                         accli.style.opacity = '0.5';
        //                                     }
        //                                 }
        //                                 else{
        //                                     ++existAccNoLength;
        //                                     accli.style.cursor = 'not-allowed';
        //                                     accli.style.pointerEvents = 'none';
        //                                     accli.style.color = '#999';
        //                                     accli.style.opacity = '0.5';
        //                                 }
        //                             }
        //                             else{
        //                             }
      }
      if (uID !== "XCEL-CO-SF") {
        await checkUAN1(item.account_number)
      } else {
        const un1val = await checkUAN1andUAN2("uan_1_value", item.account_number)
        const un2val = await checkUAN1andUAN2("uan_2_value", additionalAttributeValue)

        if ((un1val.total === 0 || un1val.total === undefined) && (un2val.total === 0 || un2val.total === undefined)) {
        } else if (un1val.total === 1 && un2val.total === 1) {
          const dealIdFromDealFoundUAN1 = un1val.results[0].id
          const dealIdFromDealFoundUAN2 = un2val.results[0].id
          const dealIdFromCurrentPage = storage.get("dealId")
          if (dealIdFromDealFoundUAN1 === dealIdFromCurrentPage && dealIdFromDealFoundUAN2 === dealIdFromCurrentPage) {
          } else {
            ++existAccNoLength
            accli.style.cursor = "not-allowed"
            accli.style.pointerEvents = "none"
            accli.style.color = "#999"
            accli.style.opacity = "0.5"
          }
        } else if (un1val.total >= 1 && un2val.total === 0) {
        } else if (un1val.total === 0 && un2val.total >= 1) {
        } else if (un1val.total >= 1 && un2val.total >= 1) {
          ++existAccNoLength
          accli.style.cursor = "not-allowed"
          accli.style.pointerEvents = "none"
          accli.style.color = "#999"
          accli.style.opacity = "0.5"
        } else {
          ++existAccNoLength
          accli.style.cursor = "not-allowed"
          accli.style.pointerEvents = "none"
          accli.style.color = "#999"
          accli.style.opacity = "0.5"
        }
      }

      accul.appendChild(accli)
      Accdiv.appendChild(accul)
      q(".have-online-details").appendChild(Accdiv)
    }
    if (existAccNoLength === responseData.length) {
      verifyidenty = "no"
      // alert("Account number already exists. Please try again.");
      passUpdatedOpenLoader()
    }
    qa(".meterWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
      li.style.display = "none"
    })
    function accountNumberSelected(event: any): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","accountNumberSelected",[event],["event"]);
      qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
        span.style.backgroundColor = "#ffffff"
      })
      qa(".meterWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
        li.style.display = "none"
      })
      if (uID === "XCEL-CO-SF") {
        qa(".premisesWrapper").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
          li.style.display = "none"
        })
      }
      const clickedElement = event.target
      firstId = clickedElement.parentElement.id
      bayouAccountNo = firstId
      const meterWrapper = clickedElement.parentElement.querySelector(".meterWrapper")
      let premisesWrapper: any
      if (uID === "XCEL-CO-SF") {
        premisesWrapper = clickedElement.parentElement.querySelector(".premisesWrapper")
      }
      if (!meterWrapper.classList.contains("hidden")) {
        if (uID !== "XCEL-CO-SF") {
          const ulElement = meterWrapper.querySelector("ul")
          const liElements = ulElement.querySelectorAll("li")
          const numberOfLi = liElements.length
          if (numberOfLi === 1) {
            verifyidenty = "yes"
            meterWrapper.style.display = "none"
            liElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
              meterId = li.id
              meterType = li.type
              if (li.tariffs) {
                rateClass = li.tariffs[0].tariff
              }
              additionalAttributeValue = li.getAttribute("aa")
            })
          } else {
            ulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
              span.style.backgroundColor = "#ffffff"
            })
            meterWrapper.style.display = "block"
            ulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
              li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[this],["this"]);
                verifyidenty = "yes"
                ulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                  span.style.backgroundColor = "#ffffff"
                })
                this.style.backgroundColor = "#f18020"
                meterId = this.id
                meterType = this.type
                if (this.tariffs) {
                  rateClass = this.tariffs[0].tariff
                }
                additionalAttributeValue = this.getAttribute("aa")
              })
            })
          }
        } else {
          const premisesulElement = premisesWrapper.querySelector("ul")
          const premisesliElements = premisesulElement.querySelectorAll("li")
          const numberOfPreLi = premisesliElements.length
          if (numberOfPreLi === 1) {
            verifyidenty = "yes"
            premisesWrapper.style.display = "none"
            premisesliElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
              //                                     meterId = li.id;
              //                                     meterType = li.type;
              //                                     if(li.tariffs){
              //                                         rateClass = li.tariffs[0].tariff;
              //                                     }
              additionalAttributeValue = li.getAttribute("aa")
            })
          } else {
            premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
              span.style.backgroundColor = "#ffffff"
              span.style.color = "#425b76"
            })
            premisesWrapper.style.display = "block"
            premisesulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
              li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[this],["this"]);
                verifyidenty = "yes"
                premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                  span.style.backgroundColor = "#ffffff"
                  span.style.color = "#425b76"
                })
                this.style.backgroundColor = "#f18020"
                this.style.color = "#ffffff"
                //                                         meterId = this.id;
                //                                         meterType = this.type;
                //                                         if(this.tariffs){
                //                                             rateClass = this.tariffs[0].tariff;
                //                                         }
                additionalAttributeValue = this.getAttribute("aa")
              })
            })
          }
        }
      } else {
        if (uID === "XCEL-CO-SF") {
          const premisesulElement = premisesWrapper.querySelector("ul")
          const premisesliElements = premisesulElement.querySelectorAll("li")
          const numberOfPreLi = premisesliElements.length
          if (numberOfPreLi === 1) {
            verifyidenty = "yes"
            premisesWrapper.style.display = "none"
            premisesliElements.forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
              //                                     meterId = li.id;
              //                                     meterType = li.type;
              //                                     if(li.tariffs){
              //                                         rateClass = li.tariffs[0].tariff;
              //                                     }
              additionalAttributeValue = li.getAttribute("aa")
            })
          } else {
            premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
              span.style.backgroundColor = "#ffffff"
              span.style.color = "#425b76"
            })
            premisesWrapper.style.display = "block"
            premisesulElement.querySelectorAll("li").forEach((li: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[li],["li"]);
              li.addEventListener("click", function (this: any) {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[this],["this"]);
                verifyidenty = "yes"
                premisesulElement.querySelectorAll("li").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
                  span.style.backgroundColor = "#ffffff"
                  span.style.color = "#425b76"
                })
                this.style.backgroundColor = "#f18020"
                this.style.color = "#ffffff"
                //                                         meterId = this.id;
                //                                         meterType = this.type;
                //                                         if(this.tariffs){
                //                                             rateClass = this.tariffs[0].tariff;
                //                                         }
                additionalAttributeValue = this.getAttribute("aa")
              })
            })
          }
        } else {
          verifyidenty = "yes"
        }
      }
      clickedElement.style.backgroundColor = "#f18020"
      qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
        span.removeAttribute("data-selected")
      })
      clickedElement.setAttribute("data-selected", "true")
    }
    qa(".accountNumbersUl li span").forEach((span: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[span],["span"]);
      span.addEventListener("click", accountNumberSelected)
    })
  }
  /* --------------- for multi enrollment ----------------------- */

  function validateAccountNumber(abc: any): string | null {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","validateAccountNumber",[abc],["abc"]);
    // var utilityAccountNo = document.getElementById("utilityAccountNo").value.trim();
    // var errorMessage = document.getElementById('utility_acc_num_error_message');
    if (abc != null && abc !== "") {
      uanSingleErrorMsgField.textContent = ""
      return null
    } else {
      if (uanSingleValue !== "") {
        const regex = new RegExp(utRgxNumberInput)
        if (regex.test(uanSingleValue)) {
          uanSingleErrorMsgField.textContent = ""
          return null
        } else {
          if (UtilityAccountNumberRegexErrorMsgInput == null || UtilityAccountNumberRegexErrorMsgInput === "") {
            uanSingleErrorMsgField.textContent = "Please enter a valid account number."
          } else {
            uanSingleErrorMsgField.textContent = UtilityAccountNumberRegexErrorMsgInput
          }
          return "error"
        }
      } else {
        if (uanFieldDisplayarray.length === 0) {
          // uanSingleErrorMsgField.textContent = '';
          return null
        } else {
          uanSingleErrorMsgField.textContent = "Please enter account number."
          return "error"
        }
      }
    }
  }
  void validateAccountNumber

  // get data from UAN config table ----------------------------------------------------
  async function getallDataFromUANConfigTable(): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","getallDataFromUANConfigTable",[],[]);
    const exampleUtilityId = storage.get("utility_id")
    const stateForUANConfig = storage.get("stateForLMIConfig")
    const utilityNameForUANConfig = storage.get("utility_Name")
    const hubDBEndpointUANC = hubdbRowsUrl("uanConfig", {
      orderBy: "uan_field_display_sequence",
      utility_id: String(exampleUtilityId),
      state_name: String(stateForUANConfig),
    })
    const responseUANC = await fetch(hubDBEndpointUANC, { method: "GET" })
    const dataUANC = await responseUANC.json()
    const rowsUANC = dataUANC.objects
    if (rowsUANC.length >= 1) {
      rowsUANC.forEach((element: any, index: number) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[element,index],["element","index"]);
        const uanFieldDisplay = element.values["4"].name
        if (uanFieldDisplay === "Yes") {
          uanFieldDefault[index] = element.values["5"]
          uanFieldDisplayarray[index] = element.values["4"].name
          const uanFieldDisSeq = element.values["6"]
          void uanFieldDisSeq
          const uanFieldLable = element.values["8"]
          uanAllLabels[index] = uanFieldLable
          const uanFieldIntDes = element.values["8"]
          uanFieldIntDesArray[index] = element.values["8"]
          const uanFieldHlpTxtHeading = element.values["9"]
          const uanFieldHlpTxtInst = element.values["10"]
          utRgxNumber[index] = element.values["11"]
          UtilityAccountNumberRegexErrorMsg[index] = element.values["12"]
          const uanFieldHelpImgTxt = element.values["13"]
          let uanHelpImg = ""
          if (element.values["14"] !== undefined) {
            uanHelpImg = element.values["14"].url
          }
          const uanConctSeq = element.values["15"]
          void uanConctSeq
          const appendField =
            '<div class="utilityAccountNoWrapper" id="utilityAccountNoWrapper' +
            index +
            '"><label id="MUuaclabel' +
            index +
            '"><span id="utilityAccountNoLabel' +
            index +
            '">' +
            uanFieldLable +
            '</span></label><input type="text" key="' +
            index +
            '" placeholder="' +
            uanFieldIntDes +
            '" class="utilityAccountNo" id="utilityAccountNo' +
            index +
            '" /><div id="utility_acc_num_error_message' +
            index +
            '" class="error-message"></div><p id="utilityAccInsTitle' +
            index +
            '">' +
            uanFieldHlpTxtHeading +
            '</p><p><span class="utilityAccIns' +
            index +
            '">' +
            uanFieldHlpTxtInst +
            '</span><br><br><a href="' +
            uanHelpImg +
            '" id="utilityBillImageAcNo' +
            index +
            '" target="_blank">' +
            uanFieldHelpImgTxt +
            ' <span class="utilityName' +
            index +
            '">' +
            utilityNameForUANConfig +
            "</span>.</a></p></div>"
          void appendField
          // var existingContent = document.querySelector('.utilityPictureIpiaap3Wrapper #utility').innerHTML;
          // var appendedContent = existingContent + appendField;
          // document.querySelector('.utilityPictureIpiaap3Wrapper #utility').innerHTML = appendedContent;
          // getUtilityNumberRegex(uanFieldReg);
        } else if (uanFieldDisplay === "No") {
          uanFieldDefault[index] = element.values["5"]
          uanFieldDisplayarray[index] = element.values["4"].name
          uanFieldIntDesArray[index] = element.values["8"]
        }
      })
      uanSingleErrorMsgField = q("#utility_acc_num_error_message0")
      if (uanSingleErrorMsgField == null || uanSingleErrorMsgField === "null") {
        uanSingleErrorMsgField = q("#utility_acc_num_error_message1")
      }
      if (uanSingleErrorMsgField == null || uanSingleErrorMsgField === "null") {
        uanSingleErrorMsgField = q("#utility_acc_num_error_message2")
      }
      _clickutilityAccountInputs = qa("#utility .utilityAccountNoWrapper .utilityAccountNo")
      const utilityAccountInputs = qa("#utility .utilityAccountNoWrapper .utilityAccountNo")
      utilityAccountInputs.forEach((input: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[input],["input"]);
        input.addEventListener("input", (event: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[event],["event"]);
          const keyAttributeValue = input.getAttribute("key")
          const inputValue = event.target.value
          uanSingleValue = inputValue
          utRgxNumberInput = utRgxNumber[keyAttributeValue]
          UtilityAccountNumberRegexErrorMsgInput = UtilityAccountNumberRegexErrorMsg[keyAttributeValue]
          uanSingleErrorMsgField = q(`#utility_acc_num_error_message${keyAttributeValue}`)
          validateAccountNumber(null)
        })
      })
    }
  }
  // get data from UAN config table ----------------------------------------------------

  async function GetStripeBillingStatus(): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","GetStripeBillingStatus",[],[]);
    // var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        deal_id: dealId,
      }
      const response = await fetch(apiUrl("GetDealRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      void catchdata
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      if (dataa.message === "No User Found") {
        // (original left this branch empty)
      } else if (dataa.message === "User Found") {
        billingStatus = dataa.userValue.properties.billing_status
        billingStatus = JSON.parse(billingStatus)
      }

      //             if(response.status == 500 || response.status == 404){
      //             }
    } catch (error) {
      console.error("Error:", error)
    }
  }
  void GetStripeBillingStatus

  // add data in deal record --------------------------------------------------------------------------------------
  async function addDataToDealRecord(globPass?: any): Promise<boolean> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","addDataToDealRecord",[globPass],["globPass"]);
    void globPass
    const email = storage.get("userEmail")
    void email
    let skipUsageSubmission: any
    let skipBayouSubmission: any
    let credentialProvider = ""
    void credentialProvider
    let iHaveOnlineAccount: any
    iHaveOnlineAccount = "Yes"
    credentialProvider = "Bayou"
    skipUsageSubmission = "No"
    skipBayouSubmission = "No"
    let uanLable1: any, uanLable2: any, uanLable3: any
    let uanValue1: any, uanValue2: any, uanValue3: any
    let isUANrow0Display = true
    let isUANrow1Display = true

    if (uanFieldDisplayarray[0] === "Yes") {
      if (uanAllLabels[0]) {
        uanLable1 = uanAllLabels[0]
      } else {
        uanLable1 = ""
      }
      uanValue1 = bayouAccountNo
    } else if (uanFieldDisplayarray[0] === "No") {
      isUANrow0Display = false
      if (uanFieldIntDesArray[0]) {
        uanLable1 = uanFieldIntDesArray[0]
      } else {
        uanLable1 = ""
      }

      if (uanFieldDefault[0]) {
        uanValue1 = uanFieldDefault[0]
      } else {
        uanValue1 = ""
      }
    }

    if (uanFieldDisplayarray[1] === "Yes") {
      if (uanAllLabels[1]) {
        uanLable2 = uanAllLabels[1]
      } else {
        uanLable2 = ""
      }
      if (isUANrow0Display === true) {
        uanValue2 = additionalAttributeValue
      } else {
        uanValue2 = bayouAccountNo
      }
    } else if (uanFieldDisplayarray[1] === "No") {
      isUANrow1Display = false
      if (uanFieldIntDesArray[1]) {
        uanLable2 = uanFieldIntDesArray[1]
      } else {
        uanLable2 = ""
      }
      if (uanFieldDefault[1]) {
        uanValue2 = uanFieldDefault[1]
      } else {
        uanValue2 = ""
      }
    }

    if (uanFieldDisplayarray[2] === "Yes") {
      if (uanAllLabels[2]) {
        uanLable3 = uanAllLabels[2]
      } else {
        uanLable3 = ""
      }
      if (isUANrow0Display === true && isUANrow1Display === true) {
        uanValue3 = allAccountNumbers[2]
      } else if (isUANrow0Display === false && isUANrow1Display === false) {
        uanValue3 = bayouAccountNo
      } else if (isUANrow0Display === true && isUANrow1Display === false) {
        uanValue3 = additionalAttributeValue
      } else if (isUANrow0Display === false && isUANrow1Display === true) {
        uanValue3 = additionalAttributeValue
      }
    } else if (uanFieldDisplayarray[2] === "No") {
      if (uanFieldIntDesArray[2]) {
        uanLable3 = uanFieldIntDesArray[2]
      } else {
        uanLable3 = ""
      }
      if (uanFieldDefault[2]) {
        uanValue3 = uanFieldDefault[2]
      } else {
        uanValue3 = ""
      }
    }

    //             var uan2LableAddAtt = "Service Number";
    //             if(additionalAttributeValue == null || additionalAttributeValue == ""){
    //                 uan2LableAddAtt = "";
    //             }
    const dealId = storage.get("dealId")
    let catchdata: any = ""

    const finalResultBox = q("#combinedMsg")
    try {
      const data = {
        properties: {
          deal_id: dealId,
          do_you_have_an_account_with_utility_service_provider: iHaveOnlineAccount,
          credential_provider: credentialProvider,
          enrollmentsessionid: enrollmentSessionID,
          skip_usage_submission: skipUsageSubmission,
          bayous_utility_id: bayouUtility,
          bayous_type: bayouTokenType,
          bayous_id: bayouId,
          bayous_onboarding_token: bayouToken,
          uan_1_label: uanLable1,
          uan_1_value: uanValue1,
          uan_2_label: uanLable2,
          uan_2_value: uanValue2,
          uan_3_label: uanLable3,
          uan_3_value: uanValue3,
          meter_id: meterId,
          utility_type: meterType,
          skip_bayou_submission: skipBayouSubmission,
          rate_class: rateClass,
          //                     bayous_tarrif: rateClass,
          tarrif_type: rateClass,
          mfa_required: mfaRequired,
          uan_2_internal_value: additionalAttributeItem,
          bayou_get_accounts_resp: accountNumberResponse,
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        isUsageSubmit = true
        if (isACHSubmit === true) {
          await updateDealStage("127892570", "achSubmit") // Submitted (Sales Pipeline)
          storage.rawSet("typ", "lMiAsTaNc")
          storage.rawSet("utilityProvider", "Bayou")
          storage.rawSet("DealStage", "Submitted")
          pageFoundCloseLoader()
          window.location.href = "/thank-you"
        } else {
          await updateDealStage("1142098957") // Verify ACH (Sales Pipeline)
          finalResultBox.textContent = "Utility verified. Please verify ACH to complete proccess."
          finalResultBox.style.color = "green"
          q(".lgsiayeu-form").style.display = "none"
          dq(".page1lmitoptitle-content.page2lgsiaeu-content h4").style.display = "none"
          pageFoundCloseLoader()
        }
      } else {
        q(".lgsiayeu-form").style.display = "block"
        dq(".page1lmitoptitle-content.page2lgsiaeu-content h4").style.display = "block"
        isUsageSubmit = false
      }
      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      isUsageSubmit = false
      pageFoundCloseLoader()
      // alert('Server error. Please try again');
      console.error("Error:", error)
    }
    return true
    // NOTE: unreachable — the original has this localStorage write after `return true`.
    storage.rawSet("uan_2_internal_value", additionalAttributeItem)
  }

  // ACH functions --------------------------------------------------------------------------------
  async function GetPaymentMethodId(): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","GetPaymentMethodId",[],[]);
    // openProgressBar();
    // var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        deal_id: dealId,
      }
      const response = await fetch(apiUrl("GetDealRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      void catchdata
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      if (dataa.message === "No User Found") {
        // (original left this branch empty)
      } else if (dataa.message === "User Found") {
        paymentMethodId = dataa.userValue.properties.stripe_payment_method_id
        paymentMethodId = paymentMethodId
        paymentMethod = dataa.userValue.properties.stripe_payment_method
        paymentId = dataa.userValue.properties.stripe_payment_id
        paymentId = paymentId
        closeProgressBar()
      }

      //             if(response.status == 500 || response.status == 404){
      //             }
    } catch (error) {
      console.error("Error:", error)
    }
  }
  function enforceTwoTwoFormat(inputId: string, errorId: string): void {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","enforceTwoTwoFormat",[inputId,errorId],["inputId","errorId"]);
    const input = qi(`#${inputId}`)
    const error = q(`#${errorId}`)

    input.addEventListener("input", (e: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[e],["e"]);
      let val = e.target.value

      // Remove all non-numeric and non-dot characters
      val = val.replace(/[^0-9.]/g, "")

      // Enforce only one dot
      const dotIndex = val.indexOf(".")
      if (dotIndex !== -1) {
        const beforeDot = val.substring(0, dotIndex).slice(0, 2) // Max 2 digits before dot
        const afterDot = val.substring(dotIndex + 1).slice(0, 2) // Max 2 digits after dot
        val = `${beforeDot}.${afterDot}`
      } else {
        // If no dot, allow max 2 digits only
        val = val.slice(0, 2)
      }

      input.value = val

      // New pattern: allow 0 to 2 digits before, exactly 2 digits after
      // Examples valid: ".33", "0.33", "00.33", "11.33"
      const validPattern = /^(\d{0,2})\.\d{2}$/
      if (!validPattern.test(val)) {
        error.textContent = "Enter a valid amount in format: .33, 0.33, or 11.33"
        input.style.borderColor = "red"
      } else {
        error.textContent = ""
        input.style.borderColor = ""
      }
    })
  }

  // ACH functions --------------------------------------------------------------------------------
  async function updateDepositAmount(value: any, amount1: any, amount2: any, res: any): Promise<boolean> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","updateDepositAmount",[value,amount1,amount2,res],["value","amount1","amount2","res"]);
    //         value = '127892573'; // Verify HU (Sales Pipeline)
    //         localStorage.setItem('DealStage', 'Submitted');
    await delay(2000)
    //         var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          dealstage: value,
          ach_deposit_amount_1: amount1,
          ach_deposit_amount_2: amount2,
          ach_verification_manual_response: res,
          billing_status: "verified",
          stripe_payment_status: "verified",
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        // (original left this branch empty)
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }
  async function updateDepositAmountWithoutStage(amount1: any, amount2: any, res: any): Promise<boolean> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","updateDepositAmountWithoutStage",[amount1,amount2,res],["amount1","amount2","res"]);
    //         value = '127892573'; // Verify HU (Sales Pipeline)
    //         localStorage.setItem('DealStage', 'Submitted');
    await delay(2000)
    //         var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          ach_deposit_amount_1: amount1,
          ach_deposit_amount_2: amount2,
          ach_verification_manual_response: res,
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        // (original left this branch empty)
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }
  async function updateDealStageACH(value: any, amount1: any, amount2: any, res: any): Promise<boolean> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","updateDealStageACH",[value,amount1,amount2,res],["value","amount1","amount2","res"]);
    //         value = '127892573'; // Verify HU (Sales Pipeline)
    //         localStorage.setItem('DealStage', 'Submitted');
    await delay(2000)
    //         var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          dealstage: value,
          ach_deposit_amount_1: amount1,
          ach_deposit_amount_2: amount2,
          ach_verification_manual_response: res,
          billing_status: "verified",
          stripe_payment_status: "verified",
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        // (original left this branch empty)
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }
  async function updateDealStage(value: any, val?: any): Promise<boolean> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","updateDealStage",[value,val],["value","val"]);
    await delay(2000)
    //         var email = localStorage.getItem("userEmail");
    const dealId = storage.get("dealId")
    let bs = "requires_action"
    // NOTE: `val = 'achSubmit'` is an assignment (never a comparison) — preserved
    // latent bug: this branch is always taken, so bs is always 'verified'.
    if ((val = "achSubmit")) {
      bs = "verified"
    } else {
      bs = "requires_action"
    }
    let catchdata: any = ""
    try {
      const data = {
        properties: {
          deal_id: dealId,
          dealstage: value,
          billing_status: bs,
          stripe_payment_status: bs,
        },
      }
      const response = await fetch(apiUrl("UpdateDealRecord"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      if (response.ok) {
        // (original left this branch empty)
      }

      const result = await response.json()
      responseFromAPI = JSON.stringify(result)
    } catch (error) {
      console.error("Error:", error)
    }
    return true
  }
  //- update deaLstage -------------------------------------------------------------------------------
  // get data from UAN config table ----------------------------------------------------
  async function getDataFromUANConfigTable(): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","getDataFromUANConfigTable",[],[]);
    const exampleUtilityId = storage.get("utility_id")
    const stateForUANConfig = storage.get("stateForLMIConfig")
    const utilityNameForUANConfig = storage.get("utility_Name")
    const hubDBEndpointUANC = hubdbRowsUrl("uanConfig", {
      orderBy: "uan_field_display_sequence",
      utility_id: String(exampleUtilityId),
      state_name: String(stateForUANConfig),
    })
    const responseUANC = await fetch(hubDBEndpointUANC, { method: "GET" })
    const dataUANC = await responseUANC.json()
    const rowsUANC = dataUANC.objects
    if (rowsUANC.length >= 1) {
      rowsUANC.forEach((element: any, index: number) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[element,index],["element","index"]);
        void index
        const uanFieldDisplaySequence = element.values["6"]
        void uanFieldDisplaySequence
        if (element.values["16"] !== undefined && element.values["16"] !== "undefined") {
          const additionalAttributes = element.values["16"]
          additionalAttributeItem = additionalAttributes
        }
      })
    }
  }
  // get data from UAN config table ----------------------------------------------------

  async function delay(ms: number): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","delay",[ms],["ms"]);
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
  async function GetContactRecord(): Promise<void> {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","GetContactRecord",[],[]);
    let email = storage.get("userEmail")
    email = (email as string).toLowerCase()
    let catchdata: any = ""
    try {
      const data = {
        email: email,
      }
      const response = await fetch(apiUrl("GetContactRecordBasedOnEmail"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
      catchdata = data
      const dataa = await response.json()
      responseFromAPI = JSON.stringify(dataa)
      const recordId = dataa.userValue.properties.hs_object_id
      const onboardToken = dataa.userValue.properties.bayou_onboarding_token
      const bayouContactId = dataa.userValue.properties.bayou_contact_id
      let is_enrolled_for_bayou = dataa.userValue.properties.is_enrolled_for_bayou
      storage.set("RecordId", recordId)
      storage.set("onboarding_token", onboardToken)
      storage.rawSet("bayou_contact_id", bayouContactId)
      if (is_enrolled_for_bayou === "yes") {
        storage.rawSet("is_enrolled_for_bayou", "yes")
      }
      is_enrolled_for_bayou = storage.rawGet("is_enrolled_for_bayou")
      if (is_enrolled_for_bayou === "yes") {
        GetDataFromDealRecordMulti(onboardToken, bayouContactId)
        getallDataFromUANConfigTable()
      } else {
        GetDataFromDealRecord()
        getallDataFromUANConfigTable()
      }
      await GetPaymentMethodId()
      if (response.status === 500 || response.status === 404) {
        // BUGFIX: original called the never-defined `enterZipcloseLoader()`
        // (a latent ReferenceError leftover from a zipcode module); the intended
        // behaviour is to close the loader.
        pageFoundCloseLoader()
      }
    } catch (error) {
      // BUGFIX: original called the never-defined `enterZipcloseLoader()`.
      pageFoundCloseLoader()
      console.error("Error:", error)
    }
  }

  // NOTE: two call sites (in getTokenAndDateStamp) pass 5 args; the trailing rest
  // param accepts and ignores the extras exactly as JS did — preserving the bug.

  // ─── Wiring (was top-level in the inline script) ──────────────────────────────
  getEmail()

  // Get utility button click ----------------------------------------------------------
  q("#get-utility-info").addEventListener("click", () => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[],[]);
    pageFoundOpenLoader()
    q("#finalCheck-error").innerHTML = ""
    if (verifyidenty === "yes") {
      ;(async () => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[],[]);
        try {
          addDataToDealRecord()
        } catch (error) {
          pageFoundCloseLoader()
        }
      })()
    } else if (verifyidenty === "no") {
      pageFoundCloseLoader()
      q("#verification-error-message").innerHTML = "Please use correct credentials before submit."
      const sectionId = "have-online-details"
      const section = q(`#${sectionId}`)
      if (section) {
        section.scrollIntoView({ behavior: "smooth" })
      }
    } else if (verifyidenty === undefined) {
      pageFoundCloseLoader()
      q("#verification-error-message").innerHTML = "Please use correct credentials before submit."
      const sectionId = "have-online-details"
      const section = q(`#${sectionId}`)
      if (section) {
        section.scrollIntoView({ behavior: "smooth" })
      }
    }
  })
  // Get utility button click ----------------------------------------------------------

  // Apply to both inputs
  enforceTwoTwoFormat("ACHDepositAmount1", "ACHDepositAmount1-error-message")
  enforceTwoTwoFormat("ACHDepositAmount2", "ACHDepositAmount2-error-message")
  q("#ach-verification-form").addEventListener("submit", async (e: any) => {
__ea_trace("modules/usage/usage-bayou-ach-verification.ts","(anon)",[e],["e"]);
    e.preventDefault()
    openProgressBar()

    const setup_intent_id = paymentId
    const amount1 = qi("#ACHDepositAmount1").value.trim()
    const amount2 = qi("#ACHDepositAmount2").value.trim()
    const resultBox = q("#finalCheck-error-ach")

    const finalResultBox = q("#combinedMsg")

    if (!setup_intent_id || !amount1 || !amount2 || Number.isNaN(amount1 as any) || Number.isNaN(amount2 as any)) {
      resultBox.textContent = "Please enter valid numbers and payment method ID."
      resultBox.style.color = "red"
      return
    }
    try {
      // EXTERNAL Stripe/ACH backend — no __ENV__ key exists, kept as literal.
      const response = await fetch("https://stripe.nautilussolar.com/stripeserver/ping", {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      })

      const res = await response.json()
      if (res.success) {
        try {
          // EXTERNAL Stripe/ACH backend — no __ENV__ key exists, kept as literal.
          const response = await fetch("https://stripe.nautilussolar.com/stripeserver/verify/setup-intent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              setup_intent_id,
              amount1,
              amount2,
            }),
          })

          const data = await response.json()
          if (data.success) {
            isACHSubmit = true
            if (isUsageSubmit === true) {
              resultBox.textContent = data.message
              resultBox.style.color = "green"
              await updateDealStageACH("127892570", amount1, amount2, JSON.stringify(data))
              storage.rawSet("typ", "lMiAsTaNc")
              storage.rawSet("DealStage", "Submitted")
              closeProgressBar()
              window.location.href = "/thank-you"
            } else {
              await updateDepositAmount("127892571", amount1, amount2, JSON.stringify(data)) // Verify Utility Info (Sales Pipeline)
              finalResultBox.textContent =
                "We have verified your payment method. Please verify utility info to complete proccess."
              finalResultBox.style.color = "black"
              q(".combinedAchWrapper").style.display = "none"
              closeProgressBar()
            }
          } else {
            await updateDepositAmountWithoutStage(amount1, amount2, JSON.stringify(data))
            isACHSubmit = false
            resultBox.textContent =
              "The two deposit amounts that you entered, do not match what Stripe deposited into your account."
            resultBox.style.color = "red"
            q(".combinedAchWrapper").style.display = "block"
            closeProgressBar()
          }
        } catch (error) {
          isACHSubmit = false
          console.error(error)
          resultBox.textContent = "Unexpected error occurred."
          resultBox.style.color = "red"
          q(".combinedAchWrapper").style.display = "block"
          closeProgressBar()
        }
      } else {
        isACHSubmit = false
        resultBox.textContent = "Server error occurred."
        resultBox.style.color = "red"
        q(".combinedAchWrapper").style.display = "block"
        closeProgressBar()
      }
    } catch (error) {
      isACHSubmit = false
      console.error(error)
      resultBox.textContent = "Server error occurred."
      resultBox.style.color = "red"
      q(".combinedAchWrapper").style.display = "block"
      closeProgressBar()
    }
  })
  // ACH functions --------------------------------------------------------------------------------

  GetContactRecord()
}
